import { UI_SHARED_BUTTON__prompt_use_english_tokens } from '@/components/shared/UI_SHARED_BUTTON__prompt_use_english_tokens';
import { use_slice_generation, use_slice_ui, use_slice_user } from '@/hooks/store/use_slices';
import { SEPARATOR__horizontal_dashed } from '@/pages/_base/separators/SEPARATOR__horizontal_dashed';
import { UTILS__graph_data } from '@/utils/UTILS__graph_data';
import { DragDropContext, Droppable, DroppableProvided, DropResult } from '@hello-pangea/dnd';
import { FIELD__list_token } from '@shared/dto/generation/graph_invoke/_fields/_primitives/list/FIELD__list_token';
import { FIELD__token } from '@shared/dto/generation/graph_invoke/_fields/token/FIELD__token';
import { FIELD_GRID2D_MODE__all_tokens_except_one } from '@shared/dto/generation/graph_invoke/_fields/token/FIELD_GRID2D_MODE__all_tokens_except_one';
import { FIELD_GRID2D_MODE__single_token_generation } from '@shared/dto/generation/graph_invoke/_fields/token/FIELD_GRID2D_MODE__single_token_generation';
import { FIELD_GRID2D_MODE__token_after_token } from '@shared/dto/generation/graph_invoke/_fields/token/FIELD_GRID2D_MODE__token_after_token';
import { FIELD_GRID2D_MODE__token_vs_token } from '@shared/dto/generation/graph_invoke/_fields/token/FIELD_GRID2D_MODE__token_vs_token';
import { Fragment } from 'react';
import { G_BUTTON__field_ui_change_visibility } from '../../_base/_buttons/G_BUTTON__field_ui_change_visibility';
import { G_UI_FIELD__token_list_item } from '../_list_item/G_UI_FIELD__token_list_item';
import { G_BUTTON__add_special_tokens } from './_buttons/G_BUTTON__add_special_tokens';
import { G_BUTTON__prompt_copy_to_clipboard } from './_buttons/G_BUTTON__prompt_copy_to_clipboard';
import { G_BUTTON__prompt_delete_all_tokens } from './_buttons/G_BUTTON__prompt_delete_all_tokens';
import { G_BUTTON__prompt_hide_unused_tokens } from './_buttons/G_BUTTON__prompt_hide_unused_tokens';
import { G_BUTTON__prompt_link_tokens } from './_buttons/G_BUTTON__prompt_link_tokens';
import { G_BUTTON__prompt_randomize_tokens_weights } from './_buttons/G_BUTTON__prompt_randomize_tokens_weights';
import { G_BUTTON__prompt_select_all_tokens } from './_buttons/G_BUTTON__prompt_select_all_tokens';
import { G_BUTTON__prompt_slider_step } from './_buttons/G_BUTTON__prompt_slider_step';
import { G_UI_FIELD_PROMPT__text_area } from './G_UI_FIELD_PROMPT__text_area';

interface PROPS {
  field: FIELD__list_token;
}

export function G_UI_FIELD__token_list({ field }: PROPS) {
  /*
   * STORE
   */

  /*
   * STORE
   */
  const { user_settings } = use_slice_user(['user_settings']);
  const { generation_current } = use_slice_generation(['generation_current']);
  const { g_general__field_to_add_special_tokens, g_hovered_grid2d_row, g_hovered_grid2d_column, g_hovered_grid2d_dimension_from_grid } = use_slice_ui([
    'g_general__field_to_add_special_tokens',
    'g_hovered_grid2d_row',
    'g_hovered_grid2d_column',
    'g_hovered_grid2d_dimension_from_grid',
  ]);

  /*

  /*
   * STATE
   */

  /*
   * EFFECTS
   */

  /*
   * EVENTS
   */

  async function onDragEnd(result: DropResult) {
    if (!result.destination) return;

    const index_from = result.source.index;
    const index_to = result.destination.index;

    let field_new = field.clone();
    field_new.items.move_item(index_from, index_to);
    field_new.field_name_and_path__fill_for_children();

    await UTILS__graph_data.update_field(generation_current.tool.graph, field_new);
  }

  /*
   * METHODS
   */

  function filter_tokens(token: FIELD__token) {
    let need_show_token = true;

    if (user_settings.generation_prompt_hide_unused_tokens) need_show_token = !token.is_hidden.get_value();

    return need_show_token;
  }

  function get_items() {
    let out = [...field.items];

    if (!g_hovered_grid2d_dimension_from_grid) {
      out = out.filter(filter_tokens);
      return out;
    }

    if (g_hovered_grid2d_row instanceof FIELD_GRID2D_MODE__token_vs_token) {
      out = field.get_tokens_by_words_english(g_hovered_grid2d_row.tokens);
    }
    //
    //
    //
    else if (g_hovered_grid2d_row instanceof FIELD_GRID2D_MODE__token_after_token) {
      out = field.get_tokens_by_words_english(g_hovered_grid2d_row.tokens);
    } else if (g_hovered_grid2d_column instanceof FIELD_GRID2D_MODE__token_after_token) {
      out = field.get_tokens_by_words_english(g_hovered_grid2d_column.tokens);
    }
    //
    //
    //
    else if (g_hovered_grid2d_column instanceof FIELD_GRID2D_MODE__single_token_generation) {
      out = field.get_tokens_by_words_english([g_hovered_grid2d_column.token_selected]);
    } else if (g_hovered_grid2d_row instanceof FIELD_GRID2D_MODE__single_token_generation) {
      out = field.get_tokens_by_words_english([g_hovered_grid2d_row.token_selected]);
    }
    //
    //
    //
    else if (g_hovered_grid2d_column instanceof FIELD_GRID2D_MODE__all_tokens_except_one) {
      out = field.get_tokens_by_words_english__except([g_hovered_grid2d_column.token_selected]);
    } else if (g_hovered_grid2d_row instanceof FIELD_GRID2D_MODE__all_tokens_except_one) {
      out = field.get_tokens_by_words_english__except([g_hovered_grid2d_row.token_selected]);
    }

    return out;
  }

  /*
   * RENDERS
   */

  function render_item(token: FIELD__token, index: number) {
    return (
      <Fragment key={token.meta.field_id}>
        <SEPARATOR__horizontal_dashed />
        <G_UI_FIELD__token_list_item {...{ field: token, index }} field_parent={field} />
      </Fragment>
    );
  }

  function render_list_header() {
    if (!g_general__field_to_add_special_tokens.is_stub()) return null;

    return (
      <div className={`flex flex-col w-full h-12 bg-bar`}>
        <div className="flex flex-row items-center justify-around w-full">
          <UI_SHARED_BUTTON__prompt_use_english_tokens />
          <G_BUTTON__prompt_select_all_tokens {...{ field }} />
          <G_BUTTON__prompt_hide_unused_tokens />
          <G_BUTTON__prompt_slider_step {...{ field_list: field }} />
        </div>
        <div
          className={`flex flex-row items-center justify-around w-full
            border-y border-dashed border-component-border
            `}
        >
          <G_BUTTON__add_special_tokens {...{ field }} />
          <G_BUTTON__prompt_copy_to_clipboard {...{ field }} />
          <G_BUTTON__prompt_link_tokens {...{ field }} />
          <G_BUTTON__prompt_randomize_tokens_weights {...{ field_list: field }} />
          <G_BUTTON__prompt_delete_all_tokens {...{ field_list: field }} />
          <G_BUTTON__field_ui_change_visibility {...{ field }} />
        </div>
      </div>
    );
  }
  function render_list() {
    return (
      <div className="flex-1 w-full overflow-y-auto bg-component">
        <DragDropContext {...{ onDragEnd }}>
          <Droppable droppableId="token-list-droppable">
            {(provided: DroppableProvided) => (
              <div ref={provided.innerRef} {...provided.droppableProps} className="flex flex-col">
                {get_items().map(render_item)}
                {provided.placeholder}
                <SEPARATOR__horizontal_dashed />
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    );
  }

  /*
   * HTML
   */

  if (generation_current.is_stub()) return <></>;
  if (!g_general__field_to_add_special_tokens.is_stub()) return null;

  return (
    <>
      <div className="flex flex-col">
        {render_list_header()}
        <G_UI_FIELD_PROMPT__text_area {...{ field_list: field }} />

        {/* LIST */}
        {render_list()}
      </div>
    </>
  );
}
